'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const faqs = [
  {
    q: 'Le programme est-il vraiment gratuit ?',
    a: 'Oui, totalement. Vous recevez 3 mois d\'accès premium sur 1 logement actif sans aucun frais. Aucune carte bancaire n\'est requise pour démarrer. La seule contrepartie est votre engagement à utiliser réellement l\'outil et à partager votre retour.',
  },
  {
    q: 'Puis-je ajouter des logements supplémentaires pendant le programme ?',
    a: 'Oui. L\'accès premium offert couvre 1 logement. Si vous souhaitez étendre à d\'autres logements pendant les 3 mois, des tarifs préférentiels partenaires s\'appliquent. Après les 3 mois, vous bénéficiez d\'une offre de continuité adaptée à votre structure.',
  },
  {
    q: 'Y a-t-il une obligation de rester après les 3 mois ?',
    a: 'Aucune. Si GiteZen ne répond pas à vos besoins, vous êtes libre de partir sans frais ni pénalité. Nous préférons des partenaires qui choisissent de rester parce que l\'outil leur apporte de la valeur, pas des partenaires captifs.',
  },
  {
    q: 'Combien de places sont disponibles par trimestre ?',
    a: 'Le nombre de places est volontairement limité pour garantir la qualité de l\'onboarding et un suivi personnalisé. Nous sélectionnons des profils complémentaires géographiquement et en termes de taille de portefeuille. Candidatez rapidement pour ne pas manquer la prochaine session.',
  },
  {
    q: 'GiteZen fonctionne avec Airbnb et Booking.com ?',
    a: 'Oui. GiteZen se synchronise avec Airbnb, Booking.com, Vrbo et d\'autres plateformes. La synchronisation des calendriers, la gestion centralisée des réservations et la messagerie automatisée fonctionnent sur toutes les plateformes connectées.',
  },
  {
    q: 'Que se passe-t-il si je n\'ai pas le temps de faire le témoignage ?',
    a: 'Le témoignage est une contrepartie attendue mais nous adaptons le format à votre disponibilité : quelques lignes écrites, un retour structuré par email, ou un court entretien de 15 minutes suffisent. Nous facilitons au maximum cet échange.',
  },
];

function FaqItem({ faq, index }: { faq: typeof faqs[0]; index: number }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-white/[0.06] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-6 text-left group"
      >
        <span className="text-cream/80 font-medium text-sm leading-relaxed group-hover:text-cream transition-colors">
          {faq.q}
        </span>
        <div
          className={`w-6 h-6 rounded-full border border-white/10 flex-shrink-0 flex items-center justify-center transition-all duration-300 mt-0.5 ${
            open ? 'bg-moss/20 border-moss/40 rotate-45' : 'bg-white/[0.03]'
          }`}
        >
          <Icon name="PlusIcon" size={12} className={open ? 'text-moss-light' : 'text-cream/40'} />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-400 ease-in-out ${
          open ? 'max-h-64 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-cream/50 text-sm leading-relaxed">{faq.a}</p>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-el').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" className="py-28 px-6 border-t border-white/[0.05]" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[2fr_3fr] gap-16 items-start">
          {/* Left */}
          <div className="lg:sticky lg:top-32">
            <div className="reveal-el">
              <p className="text-moss-light font-bold uppercase tracking-[0.25em] text-xs mb-4">Questions fréquentes</p>
              <h2 className="font-fraunces text-4xl md:text-5xl font-light italic leading-tight text-cream mb-6">
                Tout ce que<br />
                <span className="text-moss-light">vous voulez savoir.</span>
              </h2>
              <p className="text-cream/50 text-sm leading-relaxed mb-8">
                D'autres questions ? Écrivez-nous directement.
              </p>
              <a
                href="mailto:contact@full-loc.fr"
                className="inline-flex items-center gap-2 text-sm font-semibold text-moss-light hover:text-cream transition-colors"
              >
                <Icon name="EnvelopeIcon" size={16} />
                contact@full-loc.fr
              </a>
            </div>
          </div>

          {/* Right: FAQ list */}
          <div className="reveal-el glass-card rounded-2xl px-8 py-2">
            {faqs.map((faq, i) => (
              <FaqItem key={i} faq={faq} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}